import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { inventoryApi } from '@/lib/inventoryApi';
import { toNumber } from '@/lib/number';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { ReceiveStockDrawer } from '@/components/inventory/ReceiveStockDrawer';
import { AdjustStockDrawer } from '@/components/inventory/AdjustStockDrawer';
import {
  Package,
  AlertTriangle,
  XCircle,
  ArrowDownCircle,
  ArrowUpCircle,
  Filter,
  DollarSign,
} from 'lucide-react';
import { INVENTORY_CATEGORIES, STOCK_STATUS_OPTIONS } from '@/types/inventory';
import type { StockItem, StockOverview } from '@/types/inventory';
import type { ApiResponse } from '@/types/api';

export const Route = createFileRoute('/_app/inventory/')({
  component: StockDashboard,
});

function StockDashboard() {
  const { farmId, isLoading: isFarmLoading } = useCurrentFarm();
  const queryClient = useQueryClient();

  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [receiveDrawerOpen, setReceiveDrawerOpen] = useState(false);
  const [adjustDrawerOpen, setAdjustDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (categoryFilter !== 'all') p.category = categoryFilter;
    if (statusFilter !== 'all') p.status = statusFilter;
    return p;
  }, [categoryFilter, statusFilter]);

  const { data, isLoading, error, refetch } = useQuery<ApiResponse<StockOverview>>({
    queryKey: ['inventory', 'stock', farmId, params],
    queryFn: () => inventoryApi.getStock(farmId!, params),
    staleTime: 30 * 1000,
    enabled: !!farmId,
  });

  const stock = data?.data;
  const items = stock?.items ?? [];
  const summary = stock?.summary;

  function handleReceive(item?: StockItem) {
    setSelectedItem(item ?? null);
    setReceiveDrawerOpen(true);
  }

  function handleAdjust(item?: StockItem) {
    setSelectedItem(item ?? null);
    setAdjustDrawerOpen(true);
  }

  function handleMutationSuccess() {
    queryClient.invalidateQueries({ queryKey: ['inventory'] });
  }

  if (isFarmLoading || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <LoadingSpinner size={32} className="text-primary-600" />
        <p className="text-gray-500">Loading stock data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error instanceof Error ? error.message : 'Failed to load stock data'}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      {summary && <SummaryCards summary={summary} />}

      {/* Actions + Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Filter className="h-4 w-4 text-gray-400" />
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            options={[
              { value: 'all', label: 'All Categories' },
              ...INVENTORY_CATEGORIES.map((c) => ({ value: c.value, label: c.label })),
            ]}
          />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[...STOCK_STATUS_OPTIONS]}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            leftIcon={<ArrowDownCircle className="h-4 w-4" />}
            onClick={() => handleReceive()}
          >
            Receive Stock
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<ArrowUpCircle className="h-4 w-4" />}
            onClick={() => handleAdjust()}
          >
            Adjust Stock
          </Button>
        </div>
      </div>

      {/* Stock table */}
      {items.length === 0 ? (
        <Card>
          <Card.Body>
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="h-12 w-12 text-gray-300 mb-4" />
              <h3 className="font-semibold text-gray-900">No stock items found</h3>
              <p className="text-sm text-gray-500 mt-1">
                {categoryFilter !== 'all' || statusFilter !== 'all'
                  ? 'Try adjusting your filters or add items in the Items tab.'
                  : 'Get started by adding inventory items in the Items tab.'}
              </p>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <StockTable
          items={items}
          onReceive={handleReceive}
          onAdjust={handleAdjust}
        />
      )}

      {/* Drawers */}
      {farmId && (
        <>
          <ReceiveStockDrawer
            open={receiveDrawerOpen}
            onClose={() => {
              setReceiveDrawerOpen(false);
              setSelectedItem(null);
            }}
            farmId={farmId}
            preselectedItemId={selectedItem?.item_id}
            onSuccess={handleMutationSuccess}
          />
          <AdjustStockDrawer
            open={adjustDrawerOpen}
            onClose={() => {
              setAdjustDrawerOpen(false);
              setSelectedItem(null);
            }}
            farmId={farmId}
            preselectedItemId={selectedItem?.item_id}
            onSuccess={handleMutationSuccess}
          />
        </>
      )}
    </div>
  );
}

// ============================================================================
// Summary cards
// ============================================================================

function SummaryCards({ summary }: { summary: StockOverview['summary'] }) {
  const totalValue = toNumber(summary.total_value);

  const cards = [
    {
      label: 'Total Items',
      value: summary.total_items,
      icon: Package,
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600',
    },
    {
      label: 'Low Stock',
      value: summary.low_stock_count,
      icon: AlertTriangle,
      iconBg: 'bg-yellow-50',
      iconColor: 'text-yellow-600',
    },
    {
      label: 'Out of Stock',
      value: summary.out_of_stock_count,
      icon: XCircle,
      iconBg: 'bg-red-50',
      iconColor: 'text-red-600',
    },
    {
      label: 'Total Value',
      value: totalValue.toLocaleString('en-PH', { style: 'currency', currency: 'PHP' }),
      icon: DollarSign,
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => (
        <Card key={card.label}>
          <Card.Body className="flex items-center gap-4">
            <div className={`rounded-lg p-2.5 ${card.iconBg}`}>
              <card.icon className={`h-5 w-5 ${card.iconColor}`} />
            </div>
            <div>
              <p className="text-sm text-gray-500">{card.label}</p>
              <p className="text-xl font-semibold text-gray-900">{card.value}</p>
            </div>
          </Card.Body>
        </Card>
      ))}
    </div>
  );
}

// ============================================================================
// Stock table
// ============================================================================

function statusBadgeVariant(status: string) {
  switch (status) {
    case 'ok':
      return 'ok' as const;
    case 'low':
      return 'warning' as const;
    case 'out':
      return 'critical' as const;
    default:
      return 'neutral' as const;
  }
}

function statusLabel(status: string) {
  switch (status) {
    case 'ok':
      return 'OK';
    case 'low':
      return 'Low';
    case 'out':
      return 'Out';
    default:
      return status;
  }
}

interface StockTableProps {
  items: StockItem[];
  onReceive: (item: StockItem) => void;
  onAdjust: (item: StockItem) => void;
}

function StockTable({ items, onReceive, onAdjust }: StockTableProps) {
  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-gray-500">
              <th className="px-5 py-3 font-medium">Item</th>
              <th className="px-5 py-3 font-medium">Category</th>
              <th className="px-5 py-3 font-medium text-right">Quantity</th>
              <th className="px-5 py-3 font-medium text-right">Reorder Point</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium text-right">Unit Cost</th>
              <th className="px-5 py-3 font-medium text-right">Total Value</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {items.map((item) => {
              const qty = toNumber(item.quantity);
              const reorder = toNumber(item.reorder_point);
              const unitCost = item.unit_cost != null ? toNumber(item.unit_cost) : null;
              const totalVal = item.total_value != null ? toNumber(item.total_value) : null;

              return (
                <tr key={item.item_id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-3">
                    <span className="font-medium text-gray-900">{item.item_name}</span>
                    <span className="ml-1 text-gray-400 text-xs">({item.unit})</span>
                  </td>
                  <td className="px-5 py-3 text-gray-600 capitalize">{item.category}</td>
                  <td className="px-5 py-3 text-right font-medium text-gray-900">
                    {qty.toLocaleString()}
                  </td>
                  <td className="px-5 py-3 text-right text-gray-500">
                    {reorder.toLocaleString()}
                  </td>
                  <td className="px-5 py-3">
                    <Badge variant={statusBadgeVariant(item.status)} dot>
                      {statusLabel(item.status)}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-right text-gray-600">
                    {unitCost != null ? `P${unitCost.toLocaleString()}` : '-'}
                  </td>
                  <td className="px-5 py-3 text-right text-gray-600">
                    {totalVal != null ? `P${totalVal.toLocaleString()}` : '-'}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => onReceive(item)}
                        className="p-1.5 rounded-md text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                        title="Receive stock"
                      >
                        <ArrowDownCircle className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onAdjust(item)}
                        className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        title="Adjust stock"
                      >
                        <ArrowUpCircle className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
